import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../contexts/RoleContext';
import { useGame } from '../contexts/GameContext';
import { usePlayerCache } from '../contexts/PlayerCacheContext';
import * as readApi from '../api/readApi';
import { getPlayerAvailability } from '../services/playerAvailabilityService';
import { formatSkillLevel } from '../utils/formatSkillLevel';
import { formatDateTime, sortMatchesChronologically } from '../utils/dateFormat';
import { formatScheduledDateTimeInTimezone, getDateKeyInTimezone, DEFAULT_TIMEZONE } from '../utils/dateTimeUtils';
import { partitionByGroupAndStage, computeStandings } from '../utils/dashboardGroupUtils';
import { getUserDisplayName } from '../utils/nameFormat';
import PlayerNameWithApartment from '../components/PlayerNameWithApartment';
import PlayerAvailabilityModal from '../components/PlayerAvailabilityModal';
import MatchReactionStrip from '../components/MatchReactionStrip';
import MatchReactionsModal from '../components/MatchReactionsModal';
import GoogleAd from '../components/GoogleAd';
import LoadingBlock from '../components/LoadingBlock';
import './Dashboard.css';

function formatScoreSummary(scores) {
  if (!scores) return '—';
  const games = [];
  if (scores.game1) games.push(`${scores.game1.player1}-${scores.game1.player2}`);
  if (scores.game2) games.push(`${scores.game2.player1}-${scores.game2.player2}`);
  if (scores.game3) games.push(`${scores.game3.player1}-${scores.game3.player2}`);
  return games.length ? games.join(', ') : '—';
}

export default function Dashboard() {
  const { currentUser, userProfile, getProfileForGame } = useAuth();
  const userTimezone = userProfile?.timezone || DEFAULT_TIMEZONE;
  const { isAdmin } = useRole();
  const { gameId } = useGame();
  const { getPlayerName } = usePlayerCache();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTournament, setActiveTournament] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [tournamentMatches, setTournamentMatches] = useState([]);
  const [registrationsCount, setRegistrationsCount] = useState(0);
  const [registeredPlayers, setRegisteredPlayers] = useState([]);
  const [loadingTournamentData, setLoadingTournamentData] = useState(false);
  const [filterMatchType, setFilterMatchType] = useState('all');
  const [filterPlayerId, setFilterPlayerId] = useState('');
  const [filterDate, setFilterDate] = useState(() => getDateKeyInTimezone(Date.now(), DEFAULT_TIMEZONE)); // default: today in IST
  const [matchesSectionExpanded, setMatchesSectionExpanded] = useState(false);
  const [liveMatches, setLiveMatches] = useState([]);
  const [myTeamInfo, setMyTeamInfo] = useState({ teamName: null, teammateNames: [] });
  const [recentCompletedMatches, setRecentCompletedMatches] = useState([]);
  const [reactionsModalMatchId, setReactionsModalMatchId] = useState(null);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [hasAvailabilityData, setHasAvailabilityData] = useState(null); // null=not checked, true=submitted, false=needs input
  const [activeTournamentAvailability, setActiveTournamentAvailability] = useState(null); // full data for display
  const [cacheRefreshTrigger, setCacheRefreshTrigger] = useState(0);

  const todayKey = getDateKeyInTimezone(Date.now(), userTimezone);
  const userDisplayName = getUserDisplayName({
    displayName: userProfile?.displayName || currentUser?.displayName,
    email: currentUser?.email
  });

  const isApartmentAdmin = userProfile?.isApartmentAdmin ?? false;

  useEffect(() => {
    let cancelled = false;
    let intervalId;

    async function loadDashboard() {
      if (!currentUser?.uid) return;
      setLoadingTournamentData(true);
      try {
        const data = await readApi.getDashboardData(gameId);
        if (cancelled) return;
        setActiveTournament(data.activeTournament || null);
        setIsRegistered(data.isRegistered ?? false);
        setTournamentMatches(data.tournamentMatches || []);
        setRegistrationsCount(data.registrationsCount ?? 0);
        setRegisteredPlayers(data.registeredPlayers || []);
        setMyTeamInfo(data.myTeamInfo || { teamName: null, teammateNames: [] });
        setLiveMatches(data.liveMatches || []);
        setRecentCompletedMatches(data.recentCompletedMatches || []);
      } catch (e) {
        if (!cancelled) {
          console.error('Error fetching dashboard data:', e);
          setTournamentMatches([]);
          setRegistrationsCount(0);
          setRegisteredPlayers([]);
          setMyTeamInfo({ teamName: null, teammateNames: [] });
          setLiveMatches([]);
          setRecentCompletedMatches([]);
        }
      } finally {
        if (!cancelled) setLoadingTournamentData(false);
      }
    }

    loadDashboard();
    intervalId = setInterval(loadDashboard, 20000);
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [currentUser?.uid, gameId, cacheRefreshTrigger]);

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      if (detail.gameId === (gameId || 'table-tennis')) setCacheRefreshTrigger((t) => t + 1);
    };
    window.addEventListener('scheduleCachePatched', handler);
    window.addEventListener('scheduleCacheUpdated', handler);
    window.addEventListener('dashboardCacheUpdated', handler);
    return () => {
      window.removeEventListener('scheduleCachePatched', handler);
      window.removeEventListener('scheduleCacheUpdated', handler);
      window.removeEventListener('dashboardCacheUpdated', handler);
    };
  }, [gameId]);

  // When tournament asks for availability and user is registered, load their data
  useEffect(() => {
    let cancelled = false;
    async function checkAvailability() {
      if (!currentUser?.uid || !activeTournament?.id || !isRegistered || !activeTournament?.askPlayerAvailability) {
        setHasAvailabilityData(null);
        setActiveTournamentAvailability(null);
        return;
      }
      try {
        const avail = await getPlayerAvailability(activeTournament.id, currentUser.uid);
        if (cancelled) return;
        setHasAvailabilityData(avail != null);
        setActiveTournamentAvailability(avail || null);
      } catch {
        if (!cancelled) {
          setHasAvailabilityData(null);
          setActiveTournamentAvailability(null);
        }
      }
    }
    checkAvailability();
    return () => { cancelled = true; };
  }, [currentUser?.uid, activeTournament?.id, activeTournament?.askPlayerAvailability, isRegistered, showAvailabilityModal]);

  // Open availability modal once when navigated from Schedule with state.openAvailability (ref avoids circular re-runs)
  const didHandleOpenAvailability = useRef(false);
  useEffect(() => {
    if (didHandleOpenAvailability.current) return;
    if (!location.state?.openAvailability || !activeTournament?.askPlayerAvailability || !isRegistered || !currentUser?.uid) return;
    didHandleOpenAvailability.current = true;
    setShowAvailabilityModal(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state?.openAvailability, location.pathname, activeTournament?.askPlayerAvailability, isRegistered, currentUser?.uid, navigate]);

  const ongoing = tournamentMatches.filter((m) => m.status === 'scheduled' || m.status === 'in-progress');
  const completed = tournamentMatches.filter((m) => m.status === 'completed');
  const unschedulableMatches = tournamentMatches.filter((m) => m.status === 'unschedulable');
  const myUnschedulableMatches = unschedulableMatches.filter((m) => {
    const uid = currentUser?.uid;
    if (!uid) return false;
    if (m.matchType === 'singles') return m.player1Id === uid || m.player2Id === uid;
    return (m.team1Players || []).includes(uid) || (m.team2Players || []).includes(uid);
  });

  // Current user's total wins and losses in this tournament (singles + doubles)
  const { myWins, myLosses } = (() => {
    let wins = 0;
    let losses = 0;
    const uid = currentUser?.uid;
    if (!uid) return { myWins: 0, myLosses: 0 };
    for (const m of completed) {
      if (m.matchType === 'singles') {
        if (m.player1Id !== uid && m.player2Id !== uid) continue;
        const winnerId = m.winnerId || (m.winnerIds && m.winnerIds[0]);
        if (winnerId === uid) wins++;
        else losses++;
      } else {
        const inTeam1 = (m.team1Players || []).includes(uid);
        const inTeam2 = (m.team2Players || []).includes(uid);
        if (!inTeam1 && !inTeam2) continue;
        const winnerId = m.winnerId || (m.winnerIds && m.winnerIds[0]);
        const team1Won = winnerId && (m.team1Players || []).includes(winnerId);
        if (team1Won && inTeam1) wins++;
        else if (!team1Won && inTeam2) wins++;
        else losses++;
      }
    }
    return { myWins: wins, myLosses: losses };
  })();
  const myTotal = myWins + myLosses;
  const myWinRate = myTotal > 0 ? ((myWins / myTotal) * 100).toFixed(1) : '0';

  const filterMatches = (list) => {
    return list.filter((m) => {
      if (filterDate) {
        const matchDateKey = getDateKeyInTimezone(m.scheduledAt ?? m.scheduledDate, userTimezone);
        if (matchDateKey !== filterDate) return false;
      }
      if (filterMatchType === 'singles' && m.matchType !== 'singles') return false;
      if (filterMatchType === 'doubles' && m.matchType !== 'doubles') return false;
      if (filterPlayerId) {
        const pid = filterPlayerId;
        if (m.matchType === 'singles') {
          if (m.player1Id !== pid && m.player2Id !== pid) return false;
        } else {
          const in1 = (m.team1Players || []).includes(pid);
          const in2 = (m.team2Players || []).includes(pid);
          if (!in1 && !in2) return false;
        }
      }
      return true;
    });
  };

  const filteredMatches = filterMatches(tournamentMatches);
  const { groupStage, finals } = partitionByGroupAndStage(filteredMatches);

  const sections = [];
  const groupIds = (t) => Object.keys(groupStage[t] || {}).sort();
  for (const t of ['singles', 'doubles']) {
    for (const g of groupIds(t)) {
      const matches = groupStage[t][g] || [];
      const standings = computeStandings(matches, t, (id) => getPlayerName?.(id));
      sections.push({ type: 'group', matchType: t, groupId: g, title: `${t === 'singles' ? 'Singles' : 'Doubles'} — Group ${g}`, matches, standings });
    }
  }
  for (const t of ['singles', 'doubles']) {
    const matches = finals[t] || [];
    if (matches.length) {
      const standings = computeStandings(matches, t, (id) => getPlayerName?.(id));
      sections.push({ type: 'finals', matchType: t, title: `Finals — ${t === 'singles' ? 'Singles' : 'Doubles'}`, matches, standings });
    }
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <div className="dashboard-hero card-ui">
          <div className="dashboard-hero-welcome">
            <h1 className="page-title">
              Welcome, {userDisplayName || 'there'}
            </h1>
            <p className="page-subtitle dashboard-hero-meta">
              <span className="dashboard-hero-stat">{formatSkillLevel(getProfileForGame(userProfile, gameId).skillLevel)}</span>
              <span className="dashboard-hero-dot">·</span>
              <span className="dashboard-hero-stat">{getProfileForGame(userProfile, gameId).yearsOfExperience ?? 0} yrs exp</span>
            </p>
            {activeTournament && isRegistered && !loadingTournamentData && (myTeamInfo.teamName || myTeamInfo.teammateNames.length > 0) && (
              <div className="dashboard-my-team">
                <span className="dashboard-my-team-tournament">
                  In <strong>{activeTournament.name}</strong>
                </span>
                {myTeamInfo.teamName && (
                  <span className="dashboard-my-team-name">
                    <strong>Team:</strong> {myTeamInfo.teamName}
                  </span>
                )}
                {myTeamInfo.teammateNames.length > 0 && (
                  <span className="dashboard-my-teammates">
                    <strong>Teammate{myTeamInfo.teammateNames.length > 1 ? 's' : ''}:</strong>{' '}
                    {myTeamInfo.teammateNames.join(', ')}
                  </span>
                )}
              </div>
            )}
            {activeTournament && isRegistered && !loadingTournamentData && (
              <div className="dashboard-my-record">
                <span className="dashboard-my-record-label">Your record</span>
                <span className="dashboard-my-record-value dashboard-my-wins">{myWins} W</span>
                <span className="dashboard-my-record-sep">·</span>
                <span className="dashboard-my-record-value dashboard-my-losses">{myLosses} L</span>
                {myTotal > 0 && (
                  <>
                    <span className="dashboard-my-record-sep">·</span>
                    <span className="dashboard-my-record-value dashboard-my-rate">{myWinRate}% win rate</span>
                  </>
                )}
              </div>
            )}
          </div>
          {activeTournament && isRegistered && !loadingTournamentData && (
            <div className="dashboard-stats-pills">
              <div className="dashboard-pill">
                <span className="dashboard-pill-value">{registrationsCount}</span>
                <span className="dashboard-pill-label">Players</span>
              </div>
              <div className="dashboard-pill">
                <span className="dashboard-pill-value">{tournamentMatches.length}</span>
                <span className="dashboard-pill-label">Matches</span>
              </div>
              <div className="dashboard-pill dashboard-pill-ongoing">
                <span className="dashboard-pill-value">{ongoing.length}</span>
                <span className="dashboard-pill-label">Ongoing</span>
              </div>
              <div className="dashboard-pill dashboard-pill-done">
                <span className="dashboard-pill-value">{completed.length}</span>
                <span className="dashboard-pill-label">Done</span>
              </div>
            </div>
          )}
        </div>

        {currentUser && (
          <div className="dashboard-availability-prompt card-ui">
            {activeTournament?.askPlayerAvailability && isRegistered ? (
              <>
                <h3 className="dashboard-availability-prompt-title">
                  {hasAvailabilityData ? 'Edit availability' : 'Share your availability'}
                </h3>
                <p className="dashboard-availability-prompt-desc">
                  {hasAvailabilityData
                    ? `Update when you can play for ${activeTournament.name}.`
                    : `${activeTournament.name} asks players to suggest when they can play. This helps schedule matches at times that work for everyone.`}
                </p>
                <button
                  type="button"
                  className={hasAvailabilityData ? 'btn-secondary' : 'btn-primary'}
                  onClick={() => setShowAvailabilityModal(true)}
                >
                  {hasAvailabilityData ? 'Edit my availability' : 'Enter my availability'}
                </button>
              </>
            ) : (
              <>
                <h3 className="dashboard-availability-prompt-title">Your availability</h3>
                <p className="dashboard-availability-prompt-desc">
                  {activeTournament && !activeTournament.askPlayerAvailability
                    ? `${activeTournament.name} is not collecting availability. Ask your tournament admin to enable “Ask players to suggest availability” so you can set when you can play.`
                    : !activeTournament
                      ? 'Set when you can play so tournament admins can schedule matches. Join a tournament that asks for availability, or go to Schedule to see your matches.'
                      : 'Register for the active tournament to set your availability.'}
                </p>
                <button type="button" className="btn-secondary" onClick={() => navigate('/schedule')}>
                  View Schedule
                </button>
              </>
            )}
          </div>
        )}

        {myUnschedulableMatches.length > 0 && (
          <div className="dashboard-unschedulable-prompt card-ui">
            <h3 className="dashboard-unschedulable-title">⚠️ Matches need scheduling</h3>
            <p className="dashboard-unschedulable-desc">
              {myUnschedulableMatches.length} match(es) could not be scheduled because no mutual time slot was found. Add more availability so the tournament admin can reschedule.
            </p>
            <button
              type="button"
              className="btn-primary"
              onClick={() => setShowAvailabilityModal(true)}
            >
              Update my availability
            </button>
          </div>
        )}

        <GoogleAd slotKey="BANNER" className="google-ad-banner" />

        {currentUser && liveMatches.length > 0 && (
          <div className="dashboard-live-section card-ui">
            <h2 className="dashboard-section-title dashboard-live-title">
              <span className="dashboard-live-badge">Live</span>
              Current match{liveMatches.length > 1 ? 'es' : ''}
            </h2>
            <ul className="dashboard-live-list">
              {liveMatches.map((m) => {
                const isRefereeOfThis = m.refereeId === currentUser.uid;
                const scoreText = formatScoreSummary(m.scores);
                return (
                  <li key={m.id} className={`dashboard-live-item ${isRefereeOfThis ? 'dashboard-live-referee' : ''}`}>
                    <div className="dashboard-live-info">
                      <span className="dashboard-live-players">
                        {m.player1Name} vs {m.player2Name}
                      </span>
                      {isRefereeOfThis && (
                        <span className="dashboard-live-you-ref">You&apos;re refereeing</span>
                      )}
                      <span className="dashboard-live-score">{scoreText}</span>
                      <span className="dashboard-unique-views" title="Total unique views of this match score">
                        {m.viewCount ?? 0} unique views
                      </span>
                    </div>
                    {currentUser && (
                      <div className="dashboard-live-reactions">
                        <div className="dashboard-reaction-team">
                          <MatchReactionStrip
                            matchId={m.id}
                            team="player1"
                            teamName={m.player1Name || 'Player 1'}
                            reactionCounts={m.reactionCounts?.player1 || {}}
                            onReactionUpdate={(mid, team, emojiIndex, delta) => {
                              setLiveMatches((prev) => prev.map((match) => match.id !== mid ? match : {
                                ...match,
                                reactionCounts: {
                                  ...match.reactionCounts,
                                  [team]: {
                                    ...(match.reactionCounts?.[team] || {}),
                                    [emojiIndex]: Math.max(0, ((match.reactionCounts?.[team]?.[emojiIndex]) || 0) + delta)
                                  }
                                }
                              }));
                            }}
                            onOpenReactionsModal={setReactionsModalMatchId}
                          />
                        </div>
                        <div className="dashboard-reaction-team">
                          <MatchReactionStrip
                            matchId={m.id}
                            team="player2"
                            teamName={m.player2Name || 'Player 2'}
                            reactionCounts={m.reactionCounts?.player2 || {}}
                            onReactionUpdate={(mid, team, emojiIndex, delta) => {
                              setLiveMatches((prev) => prev.map((match) => match.id !== mid ? match : {
                                ...match,
                                reactionCounts: {
                                  ...match.reactionCounts,
                                  [team]: {
                                    ...(match.reactionCounts?.[team] || {}),
                                    [emojiIndex]: Math.max(0, ((match.reactionCounts?.[team]?.[emojiIndex]) || 0) + delta)
                                  }
                                }
                              }));
                            }}
                            onOpenReactionsModal={setReactionsModalMatchId}
                          />
                        </div>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => navigate(`/match/${m.id}`)}
                      className={isRefereeOfThis ? 'btn-primary dashboard-live-btn' : 'btn-secondary dashboard-live-btn'}
                    >
                      {isRefereeOfThis ? 'Score' : 'View'}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {!userProfile?.hasTournamentRegistration && recentCompletedMatches.length > 0 && (
          <div className="dashboard-recent-completed card-ui">
            <h2 className="dashboard-section-title">Recently completed matches</h2>
            <p className="dashboard-recent-subtitle">
              {userProfile?.approvedGroupIds?.length ? 'Last 5 matches for your groups' : 'Last 5 public matches'}
            </p>
            <ul className="dashboard-recent-list">
              {recentCompletedMatches.map((m) => {
                const scoreText = formatScoreSummary(m.scores);
                const completedWhen = formatDateTime(m.completedAt);
                return (
                  <li key={m.id} className="dashboard-recent-item">
                    <div className="dashboard-recent-info">
                      <span className="dashboard-recent-players">
                        {m.player1Name} vs {m.player2Name}
                      </span>
                      <span className="dashboard-recent-score">{scoreText}</span>
                      <span className="dashboard-recent-meta">{completedWhen}</span>
                      <span className="dashboard-unique-views" title="Total unique views of this match score">
                        {m.viewCount ?? 0} unique views
                      </span>
                    </div>
                    {currentUser && (
                      <div className="dashboard-recent-reactions">
                        <div className="dashboard-reaction-team">
                          <MatchReactionStrip
                            matchId={m.id}
                            team="player1"
                            teamName={m.player1Name || 'Player 1'}
                            reactionCounts={m.reactionCounts?.player1 || {}}
                            onReactionUpdate={(mid, team, emojiIndex, delta) => {
                              setRecentCompletedMatches((prev) => prev.map((match) => match.id !== mid ? match : {
                                ...match,
                                reactionCounts: {
                                  ...match.reactionCounts,
                                  [team]: {
                                    ...(match.reactionCounts?.[team] || {}),
                                    [emojiIndex]: Math.max(0, ((match.reactionCounts?.[team]?.[emojiIndex]) || 0) + delta)
                                  }
                                }
                              }));
                            }}
                            onOpenReactionsModal={setReactionsModalMatchId}
                          />
                        </div>
                        <div className="dashboard-reaction-team">
                          <MatchReactionStrip
                            matchId={m.id}
                            team="player2"
                            teamName={m.player2Name || 'Player 2'}
                            reactionCounts={m.reactionCounts?.player2 || {}}
                            onReactionUpdate={(mid, team, emojiIndex, delta) => {
                              setRecentCompletedMatches((prev) => prev.map((match) => match.id !== mid ? match : {
                                ...match,
                                reactionCounts: {
                                  ...match.reactionCounts,
                                  [team]: {
                                    ...(match.reactionCounts?.[team] || {}),
                                    [emojiIndex]: Math.max(0, ((match.reactionCounts?.[team]?.[emojiIndex]) || 0) + delta)
                                  }
                                }
                              }));
                            }}
                            onOpenReactionsModal={setReactionsModalMatchId}
                          />
                        </div>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => navigate(`/match/${m.id}`)}
                      className="btn-secondary dashboard-recent-btn"
                    >
                      View
                    </button>
                  </li>
                );
              })}
            </ul>
            <button type="button" onClick={() => navigate('/schedule')} className="btn-secondary dashboard-recent-all">
              View full schedule
            </button>
          </div>
        )}

        {activeTournament && isRegistered && (
          <div className="tournament-stats-section card-ui dashboard-card">
            <h2 className="dashboard-section-title">{activeTournament.name}</h2>
            {loadingTournamentData ? (
              <LoadingBlock message="Loading dashboard…" skeletonRows={4} />
            ) : (
              <>
                <div className="dashboard-top-grid">
                <div className="dashboard-matches-today card-ui dashboard-inner-card">
                  <h3 className="dashboard-inner-title">Matches today</h3>
                  {(() => {
                    const matchesToday = tournamentMatches.filter((m) => getDateKeyInTimezone(m.scheduledAt ?? m.scheduledDate, userTimezone) === todayKey);
                    if (matchesToday.length === 0) {
                      return <p className="dashboard-muted">No matches scheduled for today.</p>;
                    }
                    return (
                      <ul className="dashboard-today-list">
                        {matchesToday.sort(sortMatchesChronologically).map((m) => (
                          <li key={m.id} className="dashboard-today-item">
                            <span className="dashboard-today-players">
                              {m.matchType === 'doubles' && (m.team1Players || []).length > 0 ? (
                                (m.team1Players || []).map((id) => (
                                  <span key={id}><PlayerNameWithApartment playerId={id} inline /></span>
                                ))
                              ) : (
                                <PlayerNameWithApartment playerId={m.player1Id} inline />
                              )}
                              {' vs '}
                              {m.matchType === 'doubles' && (m.team2Players || []).length > 0 ? (
                                (m.team2Players || []).map((id) => (
                                  <span key={id}><PlayerNameWithApartment playerId={id} inline /></span>
                                ))
                              ) : (
                                <PlayerNameWithApartment playerId={m.player2Id} inline />
                              )}
                            </span>
                            <span className="dashboard-today-meta">
                              {formatScheduledDateTimeInTimezone(m.scheduledAt ?? m.scheduledDate, m.scheduledTime, userTimezone, { useSeparator: ' at ' })}
                              {' · '}
                              <span className={`dashboard-status-badge schedule-status-badge status-${m.status}`}>{m.status}</span>
                              {m.refereeId && (
                                <>
                                  {' · '}
                                  <span className="dashboard-today-referee">Ref: <PlayerNameWithApartment playerId={m.refereeId} inline /></span>
                                </>
                              )}
                            </span>
                            <button
                              type="button"
                              onClick={() => navigate(`/match/${m.id}`)}
                              className="dashboard-table-btn"
                            >
                              {m.status === 'scheduled' ? 'Score' : 'View'}
                            </button>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                </div>

                <div className="dashboard-quick-actions card-ui dashboard-inner-card">
                  <h3 className="dashboard-inner-title">Quick actions</h3>
                  <div className="dashboard-quick-btns">
                    <button type="button" onClick={() => navigate('/schedule')} className="btn-primary dashboard-card-btn">Schedule</button>
                    <button type="button" onClick={() => navigate(`/tournament/${activeTournament.id}/dashboard`)} className="btn-secondary dashboard-card-btn">Full dashboard</button>
                    {activeTournament?.askPlayerAvailability && (
                      <button type="button" onClick={() => setShowAvailabilityModal(true)} className="btn-secondary dashboard-card-btn">Update my availability</button>
                    )}
                  </div>
                </div>
                </div>

                <div className="dashboard-matches-block">
                  <button
                    type="button"
                    className="dashboard-matches-toggle"
                    onClick={() => setMatchesSectionExpanded(!matchesSectionExpanded)}
                    aria-expanded={matchesSectionExpanded}
                  >
                    <span>Matches by group & stage</span>
                    <span className="dashboard-matches-toggle-icon">{matchesSectionExpanded ? '▼' : '▶'}</span>
                  </button>
                  {matchesSectionExpanded && (
                  <>
                  <div className="dashboard-filters">
                    <span className="dashboard-filter-label">Date</span>
                    <button
                      type="button"
                      onClick={() => setFilterDate(todayKey)}
                      className={`dashboard-filter-btn ${filterDate === todayKey ? 'filter-active' : ''}`}
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      onClick={() => setFilterDate('')}
                      className={`dashboard-filter-btn ${!filterDate ? 'filter-active' : ''}`}
                    >
                      All dates
                    </button>
                    <input
                      type="date"
                      value={filterDate || ''}
                      onChange={(e) => setFilterDate(e.target.value || '')}
                      className="dashboard-filter-date"
                      aria-label="Filter by date"
                    />
                    <select
                      value={filterMatchType}
                      onChange={(e) => setFilterMatchType(e.target.value)}
                      className="dashboard-filter-select"
                    >
                      <option value="all">All types</option>
                      <option value="singles">Singles</option>
                      <option value="doubles">Doubles</option>
                    </select>
                    <select
                      value={filterPlayerId}
                      onChange={(e) => setFilterPlayerId(e.target.value)}
                      className="dashboard-filter-select"
                      aria-label="Filter by player"
                    >
                      <option value="">All players</option>
                      {registeredPlayers.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {sections.length === 0 ? (
                    <p className="dashboard-table-empty">No matches match the current filters.</p>
                  ) : (
                    sections.map((sec) => (
                      <div key={sec.title} className="dashboard-group-section">
                        <h5 className="dashboard-group-title">{sec.title}</h5>
                        {sec.standings.length > 0 && (
                          <div className="dashboard-standings-wrap">
                            <table className="dashboard-standings-table">
                              <thead>
                                <tr>
                                  <th>Rank</th>
                                  <th>{sec.matchType === 'doubles' ? 'Team' : 'Player'}</th>
                                  <th>W</th>
                                  <th>L</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sec.standings.map((r) => (
                                  <tr key={r.key}>
                                    <td>{r.rank}</td>
                                    <td>{r.name}</td>
                                    <td>{r.wins}</td>
                                    <td>{r.losses}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        <div className="dashboard-table-wrap">
                          <table className="dashboard-matches-table">
                            <thead>
                              <tr>
                                <th>Match #</th>
                                <th>Player / Team 1</th>
                                <th>Player / Team 2</th>
                                <th>Score</th>
                                <th>Date</th>
                                <th>Referee</th>
                                <th>Status</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {sec.matches.length === 0 ? (
                                <tr>
                                  <td colSpan="8" className="dashboard-table-empty">No matches in this group</td>
                                </tr>
                              ) : (
                                sec.matches.map((m) => (
                                  <tr key={m.id}>
                                    <td>{m.matchNumber ?? m.round ?? '—'}</td>
                                    <td>
                                      {m.matchType === 'doubles' && (m.team1Players || []).length > 0 ? (
                                        (m.team1Players || []).map((id) => (
                                          <div key={id}><PlayerNameWithApartment playerId={id} /></div>
                                        ))
                                      ) : (
                                        <PlayerNameWithApartment playerId={m.player1Id} />
                                      )}
                                    </td>
                                    <td>
                                      {m.matchType === 'doubles' && (m.team2Players || []).length > 0 ? (
                                        (m.team2Players || []).map((id) => (
                                          <div key={id}><PlayerNameWithApartment playerId={id} /></div>
                                        ))
                                      ) : (
                                        <PlayerNameWithApartment playerId={m.player2Id} />
                                      )}
                                    </td>
                                    <td>{m.status === 'completed' ? formatScoreSummary(m.scores) : '—'}</td>
                                    <td>{m.scheduledDate
                                      ? formatScheduledDateTimeInTimezone(m.scheduledAt ?? m.scheduledDate, m.scheduledTime, userTimezone, { useSeparator: ', ' })
                                      : (m.completedAt ? formatDateTime(m.completedAt) : '—')}</td>
                                    <td>{m.refereeId ? <PlayerNameWithApartment playerId={m.refereeId} /> : '—'}</td>
                                    <td>
                                      <span className={`dashboard-status-badge schedule-status-badge status-${m.status}`}>{m.status}</span>
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        onClick={() => navigate(`/match/${m.id}`)}
                                        className="dashboard-table-btn"
                                      >
                                        {m.status === 'scheduled' ? 'Score' : 'View'}
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))
                  )}
                  </>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <div className="dashboard-cards">
          {activeTournament && (
            <div className="card-ui dashboard-card">
              <h3 className="dashboard-card-title">Active Tournament</h3>
              <p>{activeTournament.name}</p>
              {!isRegistered && (
                <button
                  type="button"
                  onClick={() => navigate('/tournament/register')}
                  className="btn-primary dashboard-card-btn"
                >
                  Register Now
                </button>
              )}
              {isRegistered && (
                <div>
                  <p className="dashboard-success-text">✓ You are registered!</p>
                  <button
                    type="button"
                    onClick={() => navigate(`/tournament/${activeTournament.id}/dashboard`)}
                    className="btn-primary dashboard-card-btn"
                  >
                    View Tournament Dashboard
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/schedule')}
                    className="btn-secondary dashboard-card-btn"
                  >
                    View Schedule
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="card-ui dashboard-card">
            <h3 className="dashboard-card-title">My Matches</h3>
            <button
              type="button"
              onClick={() => navigate('/schedule')}
              className="btn-secondary dashboard-card-btn"
            >
              View Schedule
            </button>
          </div>

          <div className="card-ui dashboard-card">
            <h3 className="dashboard-card-title">Leaderboard</h3>
            <button
              type="button"
              onClick={() => navigate('/leaderboard')}
              className="btn-secondary dashboard-card-btn"
            >
              View Rankings
            </button>
          </div>

          <div className="card-ui dashboard-card">
            <h3 className="dashboard-card-title">Profile activity</h3>
            <p className="dashboard-card-desc">See when and how admins have edited your profile.</p>
            <button
              type="button"
              onClick={() => navigate('/profile/activity')}
              className="btn-secondary dashboard-card-btn"
            >
              View edit history
            </button>
          </div>

          <div className="card-ui dashboard-card">
            <h3 className="dashboard-card-title">All Tournaments</h3>
            <p className="dashboard-card-desc">View all tournaments including past and upcoming</p>
            <button
              type="button"
              onClick={() => navigate('/tournaments')}
              className="btn-secondary dashboard-card-btn"
            >
              Browse Tournaments
            </button>
          </div>

          {isApartmentAdmin && (
            <div className="card-ui dashboard-card dashboard-admin-card">
              <h3 className="dashboard-card-title">Apartment</h3>
              <button
                type="button"
                onClick={() => navigate('/groups')}
                className="btn-destructive dashboard-card-btn"
              >
                Manage my groups
              </button>
            </div>
          )}
          {isAdmin && (
            <>
              <div className="card-ui dashboard-card dashboard-admin-card">
                <h3 className="dashboard-card-title">Admin Panel</h3>
                <button
                  type="button"
                  onClick={() => navigate('/admin')}
                  className="btn-destructive dashboard-card-btn"
                >
                  Manage Users & Tournaments
                </button>
              </div>
              <div className="card-ui dashboard-card dashboard-admin-card">
                <h3 className="dashboard-card-title">Tournament Management</h3>
                <button
                  type="button"
                  onClick={() => navigate('/tournament/manage')}
                  className="btn-destructive dashboard-card-btn"
                >
                  Manage Tournament
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {reactionsModalMatchId && (() => {
        const m = liveMatches.find((match) => match.id === reactionsModalMatchId)
          || recentCompletedMatches.find((match) => match.id === reactionsModalMatchId);
        return m ? (
          <MatchReactionsModal
            matchId={reactionsModalMatchId}
            player1Name={m.player1Name || 'Player 1'}
            player2Name={m.player2Name || 'Player 2'}
            onClose={() => setReactionsModalMatchId(null)}
          />
        ) : null;
      })()}
      {showAvailabilityModal && activeTournament && currentUser && (
        <PlayerAvailabilityModal
          tournament={activeTournament}
          playerId={currentUser.uid}
          onClose={() => setShowAvailabilityModal(false)}
          onSaved={() => setHasAvailabilityData(true)}
        />
      )}
    </div>
  );
}

